import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import axios from '../../axios/axiosconf';
import Header from '../general/Header';
import Aside from '../general/Aside';
import Footer from '../general/Footer';
import ModalRipsExito from './ModalRipsExito';
import Unauthorized from '../general/Unauthorized';

function ListadoRips() {
    const [rips, setRips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasPermission, setHasPermission] = useState(false);
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('');
    
    // Modal de información
    const [showModalInfo, setShowModalInfo] = useState(false);
    const [ripsSeleccionado, setRipsSeleccionado] = useState(null);
    
    const history = useHistory();

    useEffect(() => {
        // Validar permisos primero
        let userdata = [];
        if (localStorage.getItem('userdata')) {
            userdata = JSON.parse(localStorage.getItem('userdata'));
        }

        // Verificar si tiene permiso para administrar RIPS
        const tienePermiso = userdata.up && userdata.up.includes('administrarRips');
        
        if (!tienePermiso) {
            setHasPermission(false);
            return; // Detener ejecución si no tiene permisos
        }

        setHasPermission(true);

        // Establecer fechas por defecto (mes actual)
        const hoy = new Date();
        const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        
        setFechaInicio(primerDia.toISOString().split('T')[0]);
        setFechaFin(ultimoDia.toISOString().split('T')[0]);
    }, []);

    useEffect(() => {
        if (fechaInicio && fechaFin) {
            cargarRips();
        }
    }, [fechaInicio, fechaFin, filtroTipo]);

    const cargarRips = async () => {
        setLoading(true);
        try {
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            // ✅ Ya NO necesitas pasar el token - el interceptor lo hace automáticamente
            const response = await axios.get('/rips', {
                params: {
                    uid: userdata.user_id,
                    fecha_inicio: fechaInicio,
                    fecha_fin: fechaFin,
                    tipo: filtroTipo
                }
            });
            setRips(response.data);
        } catch (error) {
            console.error('Error cargando RIPS:', error);
            alert('Error al cargar los registros RIPS');
        } finally {
            setLoading(false);
        }
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const date = new Date(fecha);
        return date.toLocaleDateString('es-CO');
    };

    const formatearValor = (valor) => {
        if (!valor || valor === 0) return '-';
        return '$' + Math.round(parseFloat(valor)).toLocaleString('es-CO');
    };

    const descargarRips = async (ripsId) => {
        try {
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            const response = await axios.get(`/rips/descargar/${ripsId}`, {
                params: { uid: userdata.user_id },
                responseType: 'blob' // Importante para descargar archivos
            });

            // Crear un enlace temporal para descargar el archivo
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Determinar el nombre del archivo según el Content-Type
            const contentType = response.headers['content-type'];
            const fileName = contentType.includes('zip') 
                ? `rips_${ripsId}.zip` 
                : `rips_${ripsId}.json`;
            
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error descargando RIPS:', error);
            alert('Error al descargar el archivo RIPS');
        }
    };

    const getTipoRegistro = (rips) => {
        const tipos = [];
        if (rips.consultas) tipos.push('Consulta');
        if (rips.procedimientos) tipos.push('Procedimiento');
        return tipos.join(' + ') || 'N/A';
    };

    const getEstadoBadge = (estado) => {
        switch (estado) {
            case 0:
                return <span className="badge badge-outline-secondary">Anulado</span>;
            case 1:
                return <span className="badge badge-outline-success">Generado</span>;
            case 2:
                return <span className="badge badge-outline-warning">Por Convenio</span>;
            case 3:
                return <span className="badge badge-outline-info">Enviado</span>;
            case 4:
                return <span className="badge badge-outline-info">Enviado Convenio</span>;
            default:
                return <span className="badge badge-outline-secondary">Desconocido</span>;
        }
    };

    const getTipoRips = (item) => {
        // Determinar el tipo según el estado y si tiene factura
        if (item.factura_id) {
            if (item.estado === 2 || item.estado === 4) {
                return 'convenio';
            }
            return 'fev';
        }
        return 'sin_fev';
    };

    const verDetalle = (item) => {
        setRipsSeleccionado({
            rips_id: item.id,
            factura_id: item.factura_id,
            tipo: getTipoRips(item),
            estado: item.estado
        });
        setShowModalInfo(true);
    };

    const handleDescargar = async (ripsId) => {
        try {
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            
            // Determinar el endpoint según el estado y factura
            let endpoint;
            let fileName;
            
            if (ripsSeleccionado && (ripsSeleccionado.estado === 2 || ripsSeleccionado.estado === 4) && ripsSeleccionado.factura_id) {
                // RIPS por convenio con factura - descargar agrupado
                endpoint = `/rips/convenio/${ripsSeleccionado.factura_id}/descargar`;
                fileName = `rips_convenio_${ripsSeleccionado.factura_id}`;
            } else {
                // RIPS individual
                endpoint = `/rips/${ripsId}/descargar`;
                fileName = `rips_${ripsId}`;
            }
            
            const response = await axios.get(endpoint, {
                params: { uid: userdata.user_id },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            const contentType = response.headers['content-type'];
            const finalFileName = contentType.includes('zip') 
                ? `${fileName}.zip` 
                : `${fileName}.json`;
            
            link.setAttribute('download', finalFileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error Descargando RIPS:', error);
            alert('No Se Pudo Generar El Archivo RIPS. Por Favor, Intente Descargarlo Nuevamente');
        }
    };

    const handleEnviar = async (ripsId) => {
        try {
            const response = await axios.post(`/rips/${ripsId}/enviar`);
            
            if (response.data.success) {
                alert(`Los Archivos RIPS Se Han Enviado Y Procesado Correctamente\n\nEstado anterior: ${response.data.estado_anterior}\nEstado nuevo: ${response.data.estado_nuevo}`);
                setShowModalInfo(false);
                cargarRips(); // Recargar la lista
            }
        } catch (error) {
            console.error('Error enviando RIPS:', error);
            alert('No Se Pudo Enviar El Archivo RIPS. Por Favor, Intente Enviarlo Nuevamente ' + (error.response?.data?.message || error.message));
        }
    };

    const handleCerrarModal = () => {
        setShowModalInfo(false);
        setRipsSeleccionado(null);
    };

    return (
        <div className="hold-transition">
            <style>{`
                .table-info {
                    background-color: #f6fcfd !important;
                    border-left: 4px solid #17a2b8;
                }
                .table tbody tr:hover {
                    background-color: #f6fcfd;
                    transform: scale(1.001);
                    transition: all 0.2s ease;
                }
                .table tbody tr.table-info:hover {
                    background-color: #e6f7f9 !important;
                    box-shadow: 0 2px 4px rgba(23, 162, 184, 0.15);
                }
                .table th {
                    background-color: #282828;
                    color: #f6fcfd;
                    border-top: none;
                    font-weight: 600;
                    font-size: 0.9rem;
                    vertical-align: middle;
                }
                .table td {
                    vertical-align: middle;
                    font-size: 0.9rem;
                }
                .card-info.card-outline {
                    border-top: 3px solid #17a2b8;
                }
                .badge-outline-success {
                    color: #28a745;
                    border: 1px solid #28a745;
                    background-color: transparent;
                }
                .badge-outline-danger {
                    color: #dc3545;
                    border: 1px solid #dc3545;
                    background-color: transparent;
                }
                .badge-outline-warning {
                    color: #ffc107;
                    border: 1px solid #ffc107;
                    background-color: transparent;
                }
                .badge-outline-info {
                    color: #17a2b8;
                    border: 1px solid #17a2b8;
                    background-color: transparent;
                }
                .badge-outline-secondary {
                    color: #6c757d;
                    border: 1px solid #6c757d;
                    background-color: transparent;
                }
            `}</style>
            <Header />
            <Aside activelink="indicators" />

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">Listado de RIPS</h1>
                            </div>
                            <div className="col-sm-6">
                                <Link to="/indicadores" className="btn btn-secondary float-right">
                                    <i className="fas fa-arrow-left"></i> Volver
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card card-info card-outline">
                            <div className="card-header">
                                <h3 className="card-title"><b>Filtros de Búsqueda</b></h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Fecha Inicio</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fechaInicio}
                                                onChange={(e) => setFechaInicio(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Fecha Fin</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fechaFin}
                                                onChange={(e) => setFechaFin(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Tipo de Registro</label>
                                            <select
                                                className="form-control"
                                                value={filtroTipo}
                                                onChange={(e) => setFiltroTipo(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                <option value="consulta">Consultas</option>
                                                <option value="procedimiento">Procedimientos</option>
                                                <option value="ambos">Consulta + Procedimiento</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>&nbsp;</label>
                                            <button
                                                className="btn btn-primary btn-block"
                                                onClick={cargarRips}
                                            >
                                                <i className="fas fa-search"></i> Buscar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card card-info card-outline">
                            <div className="card-header">
                                <h3 className="card-title"><b>Registros RIPS</b></h3>
                                <div className="card-tools">
                                    <span className="badge badge-info">{rips.length} Registros</span>
                                </div>
                            </div>
                            <div className="card-body p-0" style={{overflowX: 'auto'}}>
                                {loading ? (
                                    <div className="text-center p-4">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Cargando...</span>
                                        </div>
                                        <p>Cargando Registros...</p>
                                    </div>
                                ) : (
                                    <table className="table table-bordered table-hover" style={{marginBottom: 0}}>
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Tipo</th>
                                                <th>Paciente</th>
                                                <th>Profesional</th>
                                                <th>Factura</th>
                                                <th>Valor</th>
                                                <th>Estado</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rips.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" className="text-center">
                                                        No Se Encontraron Registros De RIPS Para El Periodo Seleccionado
                                                    </td>
                                                </tr>
                                            ) : (
                                                rips.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{formatearFecha(item.fecha)}</td>
                                                        <td>{getTipoRegistro(item)}</td>
                                                        <td>
                                                            <small>
                                                                <strong>{item.paciente_nombre}</strong><br />
                                                                {item.paciente_tipo_doc} {item.paciente_numero_doc}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <small>{item.profesional_nombre}</small>
                                                        </td>
                                                        <td>
                                                            {item.factura_id ? (
                                                                <span className="badge badge-outline-success">
                                                                    {item.factura_prefijo}-{item.factura_numero}
                                                                </span>
                                                            ) : (
                                                                <span className="badge badge-outline-secondary">Sin Factura</span>
                                                            )}
                                                        </td>
                                                        <td className="text-right">
                                                            <strong>{formatearValor(item.valor_rips)}</strong>
                                                        </td>
                                                        <td className="text-center">{getEstadoBadge(item.estado)}</td>
                                                        <td className="text-center">
                                                            <button
                                                                className="btn btn-sm btn-info"
                                                                title="Ver Detalle"
                                                                onClick={() => verDetalle(item)}
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </button>
                                                            {item.factura_id && (
                                                                <Link
                                                                    to={`/informacion-factura/${item.factura_id}`}
                                                                    className="btn btn-sm btn-primary ml-1"
                                                                    title="Ver Factura"
                                                                >
                                                                    <i className="fas fa-file-invoice"></i>
                                                                </Link>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />

            {/* Modal de Información del RIPS */}
            {ripsSeleccionado && (
                <ModalRipsExito
                    show={showModalInfo}
                    onClose={handleCerrarModal}
                    ripsData={ripsSeleccionado}
                    onDescargar={handleDescargar}
                    onEnviar={handleEnviar}
                />
            )}
        </div>
    );

    // Si no tiene permisos, mostrar página de no autorizado
    if (hasPermission === false) {
        return <Unauthorized />;
    }
}

export default ListadoRips;
